import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Minus, Plus, Trash2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { ValuePropositionBanner, Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { openWhatsApp, WHATSAPP_MESSAGES } from '../utils/whatsapp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { 
    cartItems, 
    updateQuantity, 
    removeFromCart, 
    getCartTotal, 
    userProfile, 
    clearCart 
  } = useCart();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  const [isAutoFilled, setIsAutoFilled] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  // Auto-fill from user profile (from Start Your Protocol form)
  useEffect(() => {
    if (userProfile) {
      setFormData(prev => ({
        ...prev,
        name: userProfile.name || '',
        email: userProfile.email || '',
        phone: userProfile.phone || ''
      }));
      setIsAutoFilled(true);
    }
  }, [userProfile]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (paymentError) setPaymentError(null);
  };

  const buildCartItemsPayload = () => {
    return cartItems.map(item => ({
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: item.quantity
    }));
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setPaymentError(null);
    setIsProcessing(true);

    try {
      // Step 1: Create Razorpay order on backend
      const totalPaise = getCartTotal() * 100;
      const response = await fetch(`${API_URL}/api/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: totalPaise,
          currency: 'INR',
          customer_name: formData.name,
          customer_email: formData.email,
          customer_phone: formData.phone,
          customer_address: formData.address,
          cart_items: buildCartItemsPayload()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create order');
      }

      const orderData = await response.json();

      // Step 2: Open Razorpay checkout popup
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Life Reversal',
        description: 'Order Payment',
        order_id: orderData.order_id,
        handler: async function (razorpayResponse) {
          // Step 3: Verify payment on backend
          try {
            const verifyRes = await fetch(`${API_URL}/api/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: razorpayResponse.razorpay_order_id,
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_signature: razorpayResponse.razorpay_signature,
                customer_name: formData.name,
                customer_email: formData.email,
                customer_phone: formData.phone,
                customer_address: formData.address,
                cart_items: buildCartItemsPayload()
              })
            });

            if (!verifyRes.ok) {
              throw new Error('Payment verification failed');
            }

            const verifyData = await verifyRes.json();
            setPaymentData(verifyData);
            setOrderPlaced(true);
            clearCart();
          } catch (verifyErr) {
            setPaymentError('Payment was processed but verification failed. Please contact support.');
          }
          setIsProcessing(false);
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone
        },
        theme: {
          color: '#1A3D36'
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        setPaymentError(response.error.description || 'Payment failed. Please try again.');
        setIsProcessing(false);
      });
      rzp.open();
    } catch (err) {
      setPaymentError('Unable to initiate payment. Please try again.');
      setIsProcessing(false);
    }
  };

  // --- Success State ---
  if (orderPlaced) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F2EB' }}>
        <ValuePropositionBanner />
        <Header />
        
        <div className="max-w-lg mx-auto px-6 py-24 text-center">
          <div 
            className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#1A3D36' }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h1 
            className="font-serif text-3xl mb-4"
            style={{ color: '#2B2B2B' }}
            data-testid="order-success-title"
          >
            Payment Successful
          </h1>
          <p 
            className="text-base mb-2"
            style={{ color: '#6B6B6B' }}
          >
            Your order has been confirmed. We'll contact you shortly with delivery details.
          </p>
          {paymentData && (
            <p 
              className="text-xs mb-8"
              style={{ color: '#8A8A8A' }}
              data-testid="payment-id-display"
            >
              Payment ID: {paymentData.payment_id}
            </p>
          )}
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-sm font-medium hover:opacity-70 transition-opacity"
            style={{ color: '#1A3D36' }}
            data-testid="continue-shopping-btn"
          >
            Continue Exploring
            <ArrowRight size={15} />
          </button>
        </div>

        <Footer />
      </div>
    );
  }

  // --- Empty Cart State ---
  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F2EB' }}>
        <ValuePropositionBanner />
        <Header />
        
        <div className="max-w-lg mx-auto px-6 py-24 text-center">
          <h1 
            className="font-serif text-3xl mb-4"
            style={{ color: '#2B2B2B' }}
          >
            Your cart is empty
          </h1>
          <p 
            className="text-base mb-8"
            style={{ color: '#6B6B6B' }}
          >
            Add products to start building your protocol.
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium transition-all duration-200 hover:opacity-90"
            style={{ 
              backgroundColor: '#1A3D36', 
              color: '#FFFFFF',
              borderRadius: '4px'
            }}
          >
            Shop Products
            <ArrowRight size={15} />
          </button>
        </div>

        <Footer />
      </div>
    );
  }

  // --- Main Checkout ---
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F2EB' }}>
      <ValuePropositionBanner />
      <Header />
      
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-20">
        <h1 
          className="font-serif text-3xl lg:text-4xl mb-10 text-center"
          style={{ color: '#2B2B2B' }}
        >
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          
          {/* Left - User Details Form */}
          <div>
            <h2 
              className="text-lg font-medium mb-6"
              style={{ color: '#2B2B2B' }}
            >
              Your Details
            </h2>

            {isAutoFilled && (
              <p 
                className="text-sm mb-4"
                style={{ color: '#1A3D36' }}
              >
                Details auto-filled from your profile
              </p>
            )}

            <form onSubmit={handlePlaceOrder} className="space-y-4">
              <div>
                <label 
                  className="text-xs font-medium uppercase tracking-wider block mb-2"
                  style={{ color: '#6B6B6B' }}
                >
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm rounded-sm outline-none transition-all duration-200 focus:ring-1 focus:ring-[#1A3D36]"
                  style={{ 
                    backgroundColor: '#FFFFFF', 
                    color: '#2B2B2B',
                    border: '1px solid #D9D6CF'
                  }}
                  data-testid="checkout-name"
                />
              </div>

              <div>
                <label 
                  className="text-xs font-medium uppercase tracking-wider block mb-2"
                  style={{ color: '#6B6B6B' }}
                >
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm rounded-sm outline-none transition-all duration-200 focus:ring-1 focus:ring-[#1A3D36]"
                  style={{ 
                    backgroundColor: '#FFFFFF', 
                    color: '#2B2B2B',
                    border: '1px solid #D9D6CF'
                  }}
                  data-testid="checkout-email"
                />
              </div>

              <div>
                <label 
                  className="text-xs font-medium uppercase tracking-wider block mb-2"
                  style={{ color: '#6B6B6B' }}
                >
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm rounded-sm outline-none transition-all duration-200 focus:ring-1 focus:ring-[#1A3D36]"
                  style={{ 
                    backgroundColor: '#FFFFFF', 
                    color: '#2B2B2B',
                    border: '1px solid #D9D6CF'
                  }}
                  data-testid="checkout-phone"
                />
              </div>

              <div>
                <label 
                  className="text-xs font-medium uppercase tracking-wider block mb-2"
                  style={{ color: '#6B6B6B' }}
                >
                  Address
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows={3}
                  className="w-full px-4 py-3 text-sm rounded-sm outline-none resize-none transition-all duration-200 focus:ring-1 focus:ring-[#1A3D36]"
                  style={{ 
                    backgroundColor: '#FFFFFF', 
                    color: '#2B2B2B',
                    border: '1px solid #D9D6CF'
                  }}
                  data-testid="checkout-address"
                />
              </div>

              {/* Payment Error */}
              {paymentError && (
                <div 
                  className="p-4 rounded text-sm"
                  style={{ backgroundColor: '#FEF2F2', color: '#B54848', border: '1px solid #FECACA' }}
                  data-testid="payment-error"
                >
                  {paymentError}
                </div>
              )}

              {/* Place Order Button (Mobile) */}
              <button
                type="submit"
                disabled={isProcessing}
                className="w-full py-4 text-sm font-medium transition-all duration-200 hover:opacity-90 lg:hidden mt-8"
                style={{ 
                  backgroundColor: isProcessing ? '#6B6B6B' : '#1A3D36', 
                  color: '#FFFFFF',
                  borderRadius: '4px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer'
                }}
                data-testid="place-order-mobile"
              >
                {isProcessing ? 'Processing...' : `Pay ₹${getCartTotal().toLocaleString()}`}
              </button>

              {/* WhatsApp Support (Mobile) */}
              <button
                type="button"
                onClick={() => openWhatsApp(WHATSAPP_MESSAGES.checkout)}
                className="w-full py-3 text-sm font-medium transition-all duration-200 hover:opacity-80 lg:hidden mt-3"
                style={{ 
                  backgroundColor: 'transparent', 
                  color: '#1A3D36',
                  border: '1px solid #1A3D36',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
                data-testid="checkout-whatsapp-btn-mobile"
              >
                Need help? Chat on WhatsApp
              </button>
            </form>
          </div>

          {/* Right - Order Summary */}
          <div>
            <h2 
              className="text-lg font-medium mb-6"
              style={{ color: '#2B2B2B' }}
            >
              Order Summary
            </h2>

            <div 
              className="bg-white p-6"
              style={{ border: '1px solid #E8E6E1', borderRadius: '4px' }}
            >
              {/* Cart Items */}
              <div className="space-y-4 mb-6">
                {cartItems.map(item => (
                  <div 
                    key={item.id}
                    className="flex gap-4 pb-4"
                    style={{ borderBottom: '1px solid #F0EEE9' }}
                  >
                    {/* Item Image */}
                    <div 
                      className="w-16 h-16 flex-shrink-0 flex items-center justify-center"
                      style={{ backgroundColor: '#F5F2EB' }}
                    >
                      {item.image ? (
                        <picture>
                          <source srcSet={item.image.replace(/\.(png|jpg|jpeg|PNG)$/i, '.webp')} type="image/webp" />
                          <img 
                            src={item.image} 
                            alt={item.name}
                            className="w-full h-full object-contain"
                            style={{ objectFit: 'contain', objectPosition: 'center' }}
                          />
                        </picture>
                      ) : (
                        <div className="w-full h-full" style={{ backgroundColor: '#F0EEE9' }} />
                      )}
                    </div>

                    {/* Item Details */}
                    <div className="flex-1">
                      <h3 
                        className="text-sm font-medium mb-1"
                        style={{ color: '#2B2B2B' }}
                      >
                        {item.name}
                      </h3>
                      <p 
                        className="text-sm mb-2"
                        style={{ color: '#6B6B6B' }}
                      >
                        {item.price}
                      </p>

                      {/* Quantity Controls */}
                      <div className="flex items-center justify-between">
                        <div 
                          className="flex items-center gap-2"
                          style={{ border: '1px solid #E8E6E1' }}
                        >
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="p-1.5 hover:bg-gray-50 transition-colors"
                          >
                            <Minus size={12} style={{ color: '#2B2B2B' }} />
                          </button>
                          <span 
                            className="text-xs font-medium w-5 text-center"
                            style={{ color: '#2B2B2B' }}
                          >
                            {item.quantity}
                          </span>
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="p-1.5 hover:bg-gray-50 transition-colors"
                          >
                            <Plus size={12} style={{ color: '#2B2B2B' }} />
                          </button>
                        </div>

                        <button 
                          onClick={() => removeFromCart(item.id)}
                          className="p-1 hover:opacity-70 transition-opacity"
                        >
                          <Trash2 size={14} style={{ color: '#6B6B6B' }} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between">
                  <span 
                    className="text-sm"
                    style={{ color: '#6B6B6B' }}
                  >
                    Subtotal
                  </span>
                  <span 
                    className="text-sm font-medium"
                    style={{ color: '#2B2B2B' }}
                  >
                    ₹{getCartTotal().toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span 
                    className="text-sm"
                    style={{ color: '#6B6B6B' }}
                  >
                    Shipping
                  </span>
                  <span 
                    className="text-sm"
                    style={{ color: '#6B6B6B' }}
                  >
                    Calculated at delivery
                  </span>
                </div>
              </div>

              {/* Total */}
              <div 
                className="flex items-center justify-between pt-4"
                style={{ borderTop: '1px solid #E8E6E1' }}
              >
                <span 
                  className="text-base font-medium"
                  style={{ color: '#2B2B2B' }}
                >
                  Total
                </span>
                <span 
                  className="text-xl font-semibold"
                  style={{ color: '#2B2B2B' }}
                  data-testid="checkout-total"
                >
                  ₹{getCartTotal().toLocaleString()}
                </span>
              </div>

              {/* Payment Error (Desktop) */}
              {paymentError && (
                <div 
                  className="p-4 rounded text-sm mt-4 hidden lg:block"
                  style={{ backgroundColor: '#FEF2F2', color: '#B54848', border: '1px solid #FECACA' }}
                >
                  {paymentError}
                </div>
              )}

              {/* Place Order Button (Desktop) */}
              <button
                onClick={handlePlaceOrder}
                disabled={isProcessing}
                className="w-full py-4 text-sm font-medium transition-all duration-200 hover:opacity-90 mt-6 hidden lg:block"
                style={{ 
                  backgroundColor: isProcessing ? '#6B6B6B' : '#1A3D36', 
                  color: '#FFFFFF',
                  borderRadius: '4px',
                  cursor: isProcessing ? 'not-allowed' : 'pointer'
                }}
                data-testid="place-order"
              >
                {isProcessing ? 'Processing...' : `Pay ₹${getCartTotal().toLocaleString()}`}
              </button>

              {/* WhatsApp Support */}
              <button
                type="button"
                onClick={() => openWhatsApp(WHATSAPP_MESSAGES.checkout)}
                className="w-full py-3 text-sm font-medium transition-all duration-200 hover:opacity-80 mt-3 hidden lg:block"
                style={{ 
                  backgroundColor: 'transparent', 
                  color: '#1A3D36',
                  border: '1px solid #1A3D36',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
                data-testid="checkout-whatsapp-btn"
              >
                Need help? Chat on WhatsApp
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
